import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useReviews } from "../hooks/useReviews";
import ReviewCard from "../components/ReviewCard";
import Pagination from "../components/Pagination";

export default function ReviewListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    fetchReviews,
    reviews,
    loading,
    error,
    setReviews,
    currentPage,
    totalPages,
    totalReviews,
  } = useReviews();

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    school: searchParams.get("school") || "",
    rating: searchParams.get("rating") || "",
    sortBy: searchParams.get("sortBy") || "newest",
    page: parseInt(searchParams.get("page")) || 1,
  });

  useEffect(() => {
    loadReviews();
  }, [filters]);

  const loadReviews = async () => {
    const params = {
      page: filters.page,
      limit: 10, // Số review trên 1 trang
    };

    if (filters.search) params.search = filters.search;
    if (filters.school && filters.school !== "") params.school = filters.school;
    if (filters.rating) params.rating = filters.rating;
    if (filters.sortBy) params.sortBy = filters.sortBy;

    await fetchReviews(params);
  };

  const handleFilterChange = (key, value) => {
    // Reset về trang 1 khi filter thay đổi (trừ khi thay đổi page)
    const newFilters = {
      ...filters,
      [key]: value,
      ...(key !== "page" && { page: 1 }),
    };
    setFilters(newFilters);

    // Update URL params
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== "" && v !== 1) newSearchParams.set(k, v);
    });
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page) => {
    handleFilterChange("page", page);
  };

  // Xử lý cập nhật review sau khi đánh dấu hữu ích
  const handleReviewUpdate = (reviewId, updatedReview) => {
    setReviews(
      reviews.map((review) =>
        (review._id || review.id) === reviewId
          ? { ...review, ...updatedReview }
          : review
      )
    );
  };

  return (
    <div className="review-list">
      <div className="review-list__container">
        <div className="review-list__header">
          <h1 className="review-list__title">Tất cả đánh giá</h1>
          <p className="review-list__subtitle">
            Khám phá các đánh giá từ cộng đồng về các trường học
          </p>
          {/* Hiển thị thông tin tổng số và phân trang */}
          {totalReviews > 0 && (
            <div className="review-list__stats">
              <p>
                Hiển thị {(currentPage - 1) * 10 + 1} -{" "}
                {Math.min(currentPage * 10, totalReviews)} trên {totalReviews}{" "}
                đánh giá
              </p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="review-list__filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Tìm kiếm đánh giá..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange("rating", e.target.value)}
              className="filter-select"
            >
              <option value="">Tất cả đánh giá</option>
              <option value="5">5 sao</option>
              <option value="4">4 sao trở lên</option>
              <option value="3">3 sao trở lên</option>
              <option value="2">2 sao trở lên</option>
              <option value="1">1 sao trở lên</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              className="filter-select"
            >
              <option value="newest">Mới nhất</option>
              <option value="oldest">Cũ nhất</option>
              <option value="highest">Đánh giá cao nhất</option>
              <option value="lowest">Đánh giá thấp nhất</option>
              <option value="helpful">Hữu ích nhất</option>
            </select>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="review-list__loading">
            <div className="spinner"></div>
            <p>Đang tải đánh giá...</p>
          </div>
        ) : error ? (
          <div className="review-list__error">
            <p>{error}</p>
            <button onClick={loadReviews} className="btn btn--primary">
              Thử lại
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="review-list__empty">
            <h3>Không tìm thấy đánh giá nào</h3>
            <p>Hãy thử thay đổi bộ lọc để tìm kiếm đánh giá khác</p>
          </div>
        ) : (
          <>
            <div className="review-list__items">
              {reviews.map((review) => (
                <ReviewCard
                  key={review._id || review.id}
                  review={review}
                  onReviewUpdate={handleReviewUpdate}
                />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalReviews}
              onPageChange={handlePageChange}
              itemName="đánh giá"
            />
          </>
        )}
      </div>
    </div>
  );
}
